import { ui } from '../../uiStyles';
import { rd } from './skeletons/skeletonParts';
import Skeleton from './Skeleton';
import ListRowSkeleton from './ListRowSkeleton';
import { AdminDashboardSkeleton, BillingDashboardSkeleton, OperationsDashboardSkeleton } from './skeletons/AdminBillingDashboardSkeleton';
import { FieldRoleDashboardSkeleton } from './skeletons/FieldRoleDashboardSkeleton';
import { PlatformDashboardSkeleton } from './skeletons/PlatformDashboardSkeleton';

// Flat fallback for non-role surfaces (job detail, legacy callers).
function GenericSkeleton({ kpiCount, kpiColumns, showHero, heroHeight, listRows, isMobile }) {
  const cols = kpiColumns ?? (isMobile && kpiCount === 3 ? 'repeat(3, 1fr)' : `repeat(${kpiCount}, minmax(0, 1fr))`);
  return (
    <>
      {kpiCount > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12 }}>
          {Array.from({ length: kpiCount }).map((_, i) => (
            <Skeleton key={i} height={72} borderRadius={rd.md} />
          ))}
        </div>
      ) : null}
      {showHero ? <Skeleton height={heroHeight} borderRadius={rd.lg} /> : null}
      <ListRowSkeleton rows={listRows} layout="card" />
    </>
  );
}

/**
 * Layout-faithful dashboard skeletons — dispatches to per-role modules under
 * ./skeletons/. Mirrors each role's dashboard home; keep in sync when a
 * dashboard layout changes (see CLAUDE.md skeleton parity rule).
 */
export default function InsightsDashboardSkeleton({
  variant = 'admin',
  isMobile = false,
  isTablet = false,
  padding,
  maxWidth = '1400px',
  kpiCount = 4,
  kpiColumns,
  showHero = true,
  heroHeight = 120,
  listRows = 4,
  isRoot = false,
}) {
  // Mobile shells (AppShell / BillingHome) own horizontal inset — avoid double padding.
  // A screen whose shell ALREADY pads the content (admin: AppShell mainStyle) passes `padding={0}`;
  // a screen with its own wrapper passes that wrapper's `padding` + `maxWidth`, so the skeleton's
  // card edges are the loaded cards' edges (KAN-946).
  const pad = padding ?? (isMobile ? 0 : ui.page.desktopContentPadding);

  let body;
  if (variant === 'generic') {
    body = (
      <GenericSkeleton
        kpiCount={kpiCount}
        kpiColumns={kpiColumns}
        showHero={showHero}
        heroHeight={heroHeight}
        listRows={listRows}
        isMobile={isMobile}
      />
    );
  } else if (variant === 'billing') {
    body = <BillingDashboardSkeleton isMobile={isMobile} isTablet={isTablet} />;
  } else if (variant === 'worker') {
    body = <FieldRoleDashboardSkeleton isMobile={isMobile} isTablet={isTablet} />;
  } else if (variant === 'supervisor') {
    body = <FieldRoleDashboardSkeleton isSupervisor isMobile={isMobile} isTablet={isTablet} />;
  } else if (variant === 'platform') {
    body = <PlatformDashboardSkeleton isMobile={isMobile} isTablet={isTablet} isRoot={isRoot} />;
  } else if (variant === 'operations') {
    body = <OperationsDashboardSkeleton isMobile={isMobile} isTablet={isTablet} />;
  } else {
    body = <AdminDashboardSkeleton isMobile={isMobile} isTablet={isTablet} />;
  }

  return (
    <div
      style={{
        padding: pad,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxWidth,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
      aria-busy="true"
      data-testid="dashboard-load-skeleton"
      data-skeleton-variant={variant}
    >
      {body}
    </div>
  );
}
